import { useTranslation } from 'react-i18next';
import { useOrgMember } from '@/api/queries/useOrgMembers';
import { notFoundOrUnknownKey } from '@/api/errors';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { BackButton } from '@/components/back-button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  EntityDetailTitle,
  Skeleton,
  Page,
  PageContent
} from '@bliks-io/ui';

interface OrgMemberDetailPageProps {
  orgMemberId: string;
}

export function OrgMemberDetailPage({ orgMemberId }: OrgMemberDetailPageProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { data: orgMember, isPending, error } = useOrgMember(orgMemberId);

  useDocumentTitle('pageTitles.orgMemberDetail', {
    name: orgMember ? `${orgMember.firstName} ${orgMember.lastName}` : 'Loading...'
  });

  if (isPending) {
    return (
      <Page>
        <PageContent>
          <Skeleton className="w-64 h-8 mb-8" />
          <Card>
            <CardHeader>
              <Skeleton className="w-48 h-6" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-2/3 h-4" />
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  if (error || !orgMember) {
    return (
      <Page>
        <PageContent>
          <div className="flex items-center gap-4 mb-8">
            <BackButton to="/$slug/organization/org-members" params={{ slug }} />
          </div>
          <Card>
            <CardContent className="p-6">
              <p className="text-destructive">
                {t(notFoundOrUnknownKey(error, 'organization.members.notFound'))}
              </p>
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageContent>
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/$slug/organization/org-members" params={{ slug }} />
          <div>
            <EntityDetailTitle
              name={`${orgMember.firstName} ${orgMember.lastName}`}
              deletedAt={orgMember.deletedAt}
            />
            <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
              {t('organization.members.detailDescription')}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('organization.members.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-8">
                    {t('organization.members.firstName')}
                  </label>
                  <p className="mt-1 text-base">{orgMember.firstName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-8">
                    {t('organization.members.lastName')}
                  </label>
                  <p className="mt-1 text-base">{orgMember.lastName}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Email')}</label>
                <p className="mt-1 text-base">{orgMember.email}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t('common.entityTypes.Department')}
                </label>
                <p className="mt-1 text-base">{orgMember.department?.name || '-'}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t('organization.members.location')}
                </label>
                <p className="mt-1 text-base">{orgMember.location || '-'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </Page>
  );
}
